import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import {
  errorText,
  formatError,
  result,
  validation,
  validationError,
} from './shared.ts';

async function collect(): Promise<RawData> {
  return {
    width: screen.width,
    height: screen.height,
    availableWidth: screen.availWidth,
    availableHeight: screen.availHeight,
    colorDepth: screen.colorDepth,
    pixelDepth: screen.pixelDepth,
    devicePixelRatio: window.devicePixelRatio,
    orientationType: screen.orientation?.type ?? null,
    orientationAngle: screen.orientation?.angle ?? null,
  };
}

function validateScreen(rawData: RawData): ValidationResult {
  const reasons: string[] = [];
  const width = Number(rawData.width);
  const height = Number(rawData.height);
  const dpr = Number(rawData.devicePixelRatio);
  if (
    !Number.isFinite(width) ||
    width < 320 ||
    width > 16384 ||
    !Number.isFinite(height) ||
    height < 240 ||
    height > 16384
  ) {
    reasons.push('Screen dimensions are outside normal bounds');
  }
  if (!Number.isFinite(dpr) || dpr < 0.5 || dpr > 8) {
    reasons.push('Device pixel ratio is outside normal bounds');
  }
  if (![24, 30, 32].includes(Number(rawData.colorDepth))) {
    reasons.push('Color depth is not a normal Chrome value');
  }
  return validation(reasons);
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return `${String(rawData.width)}×${String(rawData.height)} @ ${String(rawData.devicePixelRatio)}x`;
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validateScreen(rawData);
}

export const screenDprTest: TestDefinition = {
  id: 'screenDpr',
  title: 'Screen / DPR',
  category: 'shared',
  format,
  run,
  validate,
};
